/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  PenaltyTransactionInitialProperties,
  PenaltyTransactionResponseInterface,
  RequestPenaltyTransactionInterFace,
} from '../../interface/penalty-transaction-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import penaltyManagementApi from '../../utils/api/penalty-management';

/* ------------------ THUNKS ------------------ */
export const fetchPenaltyTransaction = createAsyncThunk<
  PenaltyTransactionResponseInterface,
  RequestPenaltyTransactionInterFace
>('penalty-transaction/fetchPenaltyTransaction', async (data, { dispatch }) => {
  try {
    const response = await penaltyManagementApi.fetchTransactionList(removeEmptyKeys({ ...data }));
    return response.data;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

export const fetchPenaltyTransactionFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'penalty-transaction/fetchPenaltyTransactionFilters',
  async (_, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.fetchTransactionListFilters();
      return ConfigureListFilterResponse(response.data);
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

/* ------------------ DEFAULT FILTERS ------------------ */
const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

/* ------------------ INITIAL STATE ------------------ */
const initialState: PenaltyTransactionInitialProperties = {
  penaltyTransaction: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const PenaltyTransactionSlice = createSlice({
  name: 'penalty-transaction',
  initialState,
  reducers: {
    /**
     * Update By month filters
     * @param state
     * @param action
     */
    updatePenaltyTransactionFilters: (
      state: { filters: CommonFilterProperties[]; isFilterUpdated: boolean },
      action: PayloadAction<FilterUpdateProperties>,
    ) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },

    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disablePemaltyTransactionFilterApplied: (state: { isFilterUpdated: boolean }) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortDate: (
      state: { sortField: string; sortBy: string },
      action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>,
    ) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<PenaltyTransactionInitialProperties>) => {
    builder
      .addCase(fetchPenaltyTransaction.pending, (state: PenaltyTransactionInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchPenaltyTransaction.fulfilled,
        (
          state: PenaltyTransactionInitialProperties,
          action: PayloadAction<PenaltyTransactionResponseInterface>,
        ) => {
          state.loading = false;
          state.penaltyTransaction = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchPenaltyTransaction.rejected, (state: PenaltyTransactionInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchPenaltyTransactionFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchPenaltyTransactionFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchPenaltyTransactionFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export actions
export const {
  updatePenaltyTransactionFilters,
  disablePemaltyTransactionFilterApplied,
  setSortDate,
} = PenaltyTransactionSlice.actions;
// Export the reducer
export default PenaltyTransactionSlice.reducer;
